import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiPhone, FiClock, FiMail, FiNavigation } = FiIcons;

const Contact = () => {
  const contactInfo = [
    {
      icon: FiMapPin,
      title: 'Location',
      details: ['No 65/C New Town Road', 'Embilipitiya, Sri Lanka'],
      action: 'Get Directions'
    },
    {
      icon: FiPhone,
      title: 'Phone',
      details: ['+94 47 2 260 123', '+94 77 123 4567'],
      action: 'Call Now'
    },
    {
      icon: FiClock,
      title: 'Hours',
      details: ['Mon - Sat: 9:00 AM - 6:00 PM', 'Sunday: Closed'],
      action: 'Book Appointment'
    },
    {
      icon: FiMail,
      title: 'Email',
      details: ['info@mahanamaopticians.lk', 'appointments@mahanamaopticians.lk'],
      action: 'Send Email'
    }
  ];

  return (
    <section id="contact" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Get In <span className="gradient-text">Touch</span>
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Visit us in the heart of Embilipitiya's New Town for all your eye care needs
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="glass-card p-6 flex items-start space-x-4"
              >
                <div className="glass-icon">
                  <SafeIcon icon={info.icon} className="text-2xl text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">{info.title}</h3>
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-white/70 mb-1">{detail}</p>
                  ))}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="mt-3 text-blue-400 hover:text-blue-300 font-medium transition-colors"
                  >
                    {info.action}
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Map & Directions */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Map Placeholder */}
            <div className="glass-card p-8 text-center">
              <SafeIcon icon={FiNavigation} className="text-6xl text-blue-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Find Us Easily</h3>
              <p className="text-white/70 mb-6">
                We're conveniently located in New Town, opposite the main bus stand. 
                Look for our distinctive blue signage.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="glass-button-primary px-6 py-3 text-white font-medium"
              >
                Open in Google Maps
              </motion.button>
            </div>

            {/* Quick Contact Form */}
            <div className="glass-card p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Quick Inquiry</h3>
              <form className="space-y-4">
                <div>
                  <input
                    type="text"
                    placeholder="Your Name"
                    className="glass-input w-full p-3 text-white placeholder-white/50"
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    className="glass-input w-full p-3 text-white placeholder-white/50"
                  />
                </div>
                <div>
                  <textarea
                    placeholder="Your Message"
                    rows="4"
                    className="glass-input w-full p-3 text-white placeholder-white/50 resize-none"
                  ></textarea>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="glass-button-primary w-full py-3 text-white font-medium"
                >
                  Send Message
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="glass-card p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">Parking & Accessibility</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-white/70">
              <div>
                <h4 className="font-semibold text-white mb-2">Parking</h4>
                <p>Ample free parking available in front of our store</p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Public Transport</h4>
                <p>Easily accessible by bus - stop right outside</p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Accessibility</h4>
                <p>Wheelchair accessible entrance and facilities</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;